import { useEffect, useRef, useState } from 'react';
import { SquareEnvironment } from '../../../api';

export enum SquareIntent {
  CHARGE = 'CHARGE',
  CHARGE_AND_STORE = 'CHARGE_AND_STORE',
  STORE = 'STORE',
}

type SquareVerificationDetails = {
  amount?: string;
  currencyCode?: string;
  intent: SquareIntent;
  billingContact: { givenName: string; familyName: string; email: string };
  customerInitiated: boolean;
  sellerKeyedIn: boolean;
};

type SquareTokenizeResult = {
  status: string;
  token?: string;
  errors?: { type: string; message: string }[];
};

type SquareCard = {
  attach: (container: HTMLElement) => Promise<void>;
  tokenize: (verificationDetails: SquareVerificationDetails) => Promise<SquareTokenizeResult>;
  destroy: () => Promise<boolean>;
};

type SquareCardOptions = { style?: Record<string, Record<string, string>> };

declare global {
  interface Window {
    Square?: {
      payments: (
        applicationId: string,
        locationId: string,
      ) => { card: (options?: SquareCardOptions) => Promise<SquareCard> };
    };
  }
}

const SQUARE_SDK_URLS: Record<SquareEnvironment, string> = {
  [SquareEnvironment.PRODUCTION]: 'https://web.squarecdn.com/v1/square.js',
  [SquareEnvironment.SANDBOX]: 'https://sandbox.web.squarecdn.com/v1/square.js',
};

const TOKENIZE_STATUS_OK = 'OK';
const TOKENIZE_STATUS_INVALID = 'Invalid';
const TOKENIZE_ERROR_VERIFICATION = 'VERIFICATION_ERROR';

const INVALID_PROVIDER_ERROR_IDS = [
  'CVV_FAILURE',
  'INVALID_CARD',
  'INVALID_EXPIRATION',
  'ADDRESS_VERIFICATION_FAILURE',
];
const VERIFICATION_PROVIDER_ERROR_IDS = ['CARD_DECLINED_VERIFICATION_REQUIRED', 'VERIFICATION_FAILED'];

export const SQUARE_ERROR_TEXT_KEYS = {
  declined: 'order_page.square.errors.declined',
  invalid: 'order_page.square.errors.invalid',
  verificationFailed: 'order_page.square.errors.verificationFailed',
  unavailable: 'order_page.square.errors.unavailable',
  chargedNotBooked: 'order_page.square.errors.chargedNotBooked',
  alreadyPaid: 'order_page.square.errors.alreadyPaid',
  doubleCharge: 'order_page.square.errors.doubleCharge',
  unknownOutcome: 'order_page.square.errors.unknownOutcome',
};

export const CHECKOUT_EXPIRED_PROVIDER_ERROR_ID = 'checkout_expired';
export const CHARGED_NOT_BOOKED_PROVIDER_ERROR_ID = 'charged_not_booked';
export const UNKNOWN_OUTCOME_PROVIDER_ERROR_ID = 'unknown_outcome';
export const ALREADY_PAID_PROVIDER_ERROR_ID = 'already_paid';
export const DOUBLE_CHARGE_PROVIDER_ERROR_ID = 'double_charge';
const CONNECTION_LOST_PROVIDER_ERROR_ID = 'connection_lost';

const scriptLoads: Partial<Record<SquareEnvironment, Promise<void>>> = {};

const loadSquareScript = (environment: SquareEnvironment): Promise<void> => {
  const existingLoad = scriptLoads[environment];
  if (existingLoad) {
    return existingLoad;
  }
  const load = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SQUARE_SDK_URLS[environment];
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      delete scriptLoads[environment];
      reject(new Error('Square SDK failed to load'));
    };
    document.head.appendChild(script);
  });
  scriptLoads[environment] = load;
  return load;
};

export const providerErrorId = (error: any): string | undefined => error?.body?.detail?.provider_error?.id;

// Declined ids (CARD_DECLINED, GENERIC_DECLINE, INSUFFICIENT_FUNDS, CARD_EXPIRED) and every other card failure
// Square reports fall through to the declined text. Only an unreachable service reads as unavailable.
export const squareProviderErrorTextKey = (error: any): string => {
  const errorId = providerErrorId(error);
  if (errorId === CHARGED_NOT_BOOKED_PROVIDER_ERROR_ID) {
    return SQUARE_ERROR_TEXT_KEYS.chargedNotBooked;
  }
  if (errorId === ALREADY_PAID_PROVIDER_ERROR_ID) {
    return SQUARE_ERROR_TEXT_KEYS.alreadyPaid;
  }
  if (errorId === DOUBLE_CHARGE_PROVIDER_ERROR_ID) {
    return SQUARE_ERROR_TEXT_KEYS.doubleCharge;
  }
  if (errorId === CONNECTION_LOST_PROVIDER_ERROR_ID) {
    return SQUARE_ERROR_TEXT_KEYS.unavailable;
  }
  if (errorId && INVALID_PROVIDER_ERROR_IDS.includes(errorId)) {
    return SQUARE_ERROR_TEXT_KEYS.invalid;
  }
  if (errorId && VERIFICATION_PROVIDER_ERROR_IDS.includes(errorId)) {
    return SQUARE_ERROR_TEXT_KEYS.verificationFailed;
  }
  if (errorId && errorId !== 'unavailable' && error?.status !== 502) {
    return SQUARE_ERROR_TEXT_KEYS.declined;
  }
  return SQUARE_ERROR_TEXT_KEYS.unavailable;
};

export const tokenizeSquareCard = async (
  card: SquareCard,
  verificationDetails: SquareVerificationDetails,
): Promise<{ token: string } | { errorTextKey: string }> => {
  try {
    const result = await card.tokenize(verificationDetails);
    if (result.status === TOKENIZE_STATUS_OK && result.token) {
      return { token: result.token };
    }
    if (result.errors?.some((error) => error.type === TOKENIZE_ERROR_VERIFICATION)) {
      return { errorTextKey: SQUARE_ERROR_TEXT_KEYS.verificationFailed };
    }
    if (result.status === TOKENIZE_STATUS_INVALID) {
      return { errorTextKey: SQUARE_ERROR_TEXT_KEYS.invalid };
    }
    return { errorTextKey: SQUARE_ERROR_TEXT_KEYS.unavailable };
  } catch (e) {
    console.error(e);
    return { errorTextKey: SQUARE_ERROR_TEXT_KEYS.unavailable };
  }
};

type SquareSdkParams = {
  environment: SquareEnvironment;
  applicationId: string;
  locationId: string;
  cardStyle?: SquareCardOptions['style'];
};

export const useSquareSdk = ({ environment, applicationId, locationId, cardStyle }: SquareSdkParams) => {
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const cardStyleRef = useRef(cardStyle);
  const [card, setCard] = useState<SquareCard | null>(null);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    let attachedCard: SquareCard | undefined;
    const attachCard = async () => {
      try {
        await loadSquareScript(environment);
        const newCard = await window.Square!.payments(applicationId, locationId).card({
          style: cardStyleRef.current,
        });
        if (isCancelled || !cardContainerRef.current) {
          await newCard.destroy();
          return;
        }
        await newCard.attach(cardContainerRef.current);
        if (isCancelled) {
          await newCard.destroy();
          return;
        }
        attachedCard = newCard;
        setCard(newCard);
      } catch (e) {
        console.error(e);
        if (!isCancelled) {
          setHasLoadFailed(true);
        }
      }
    };
    void attachCard();
    return () => {
      isCancelled = true;
      setCard(null);
      void attachedCard?.destroy();
    };
  }, [environment, applicationId, locationId]);

  return { cardContainerRef, card, hasLoadFailed };
};
